'use client'

import React, { useState } from 'react'
import { StrategyParameter } from '@/types'
import { 
  Plus, 
  Edit3, 
  Trash2, 
  Save, 
  X, 
  Settings,
  ToggleLeft,
  ToggleRight
} from 'lucide-react'
import { addStrategyParameter, updateStrategyParameter, deleteStrategyParameter } from '../lib/api'
import Toast from './Toast'
import ConfirmDialog from './ConfirmDialog'

interface ParameterConfigProps {
  parameters: StrategyParameter[]
  strategyId: string
  onUpdate?: () => void
}

const ParameterConfig: React.FC<ParameterConfigProps> = ({ parameters, strategyId, onUpdate }) => {
  const [editingParam, setEditingParam] = useState<string | null>(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [deleteParamId, setDeleteParamId] = useState<string | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    default_value: '',
    range_min: '',
    range_max: '',
    tunable: true,
    parameter_type: 'number'
  })

  const handleEdit = (paramId: string) => {
    const param = parameters.find(p => p.id === paramId)
    if (param) {
      setFormData({
        name: param.name,
        description: param.description || '',
        default_value: param.default_value,
        range_min: param.range_min?.toString() || '',
        range_max: param.range_max?.toString() || '',
        tunable: param.tunable,
        parameter_type: param.parameter_type
      })
      setEditingParam(paramId)
    }
  }

  const handleSave = async () => {
    try {
      setLoading(true)
      
      if (editingParam) {
        // 更新现有参数
        await updateStrategyParameter({
          parameter_id: editingParam,
          ...formData
        })
      } else {
        // 添加新参数
        await addStrategyParameter({
          strategy_id: strategyId,
          ...formData
        })
      }
      
      // 重置表单状态
      setEditingParam(null)
      setShowAddForm(false)
      setFormData({
        name: '',
        description: '',
        default_value: '',
        range_min: '',
        range_max: '',
        tunable: true,
        parameter_type: 'number'
      })
      
      // 显示成功消息
      setToastMessage('参数保存成功！')
      setShowToast(true)
      
      // 通知父组件更新数据
      if (onUpdate) {
        onUpdate()
      }
      
    } catch (error) {
      console.error('保存参数失败:', error)
      setToastMessage('保存参数失败，请稍后重试')
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (paramId: string) => {
    setDeleteParamId(paramId)
    setShowConfirmDialog(true)
  }

  const confirmDelete = async () => {
    if (!deleteParamId) return
    
    try {
      setLoading(true)
      await deleteStrategyParameter(deleteParamId)
      
      // 显示成功消息
      setToastMessage('参数删除成功！')
      setShowToast(true)
      
      // 通知父组件更新数据
      if (onUpdate) {
        onUpdate()
      }
      
    } catch (error) {
      console.error('删除参数失败:', error)
      setToastMessage('删除参数失败，请稍后重试')
      setShowToast(true)
    } finally {
      setLoading(false)
      setShowConfirmDialog(false)
      setDeleteParamId(null)
    }
  }

  const handleCancel = () => {
    setEditingParam(null)
    setShowAddForm(false)
    setFormData({
      name: '',
      description: '',
      default_value: '',
      range_min: '',
      range_max: '',
      tunable: true,
      parameter_type: 'number'
    })
  }

  const getParameterTypeLabel = (type: string) => {
    const typeMap: Record<string, string> = {
      number: '数值',
      string: '字符串',
      boolean: '布尔值'
    }
    return typeMap[type] || type
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gradient">参数配置</h2>
        <button
          onClick={() => setShowAddForm(true)}
          className="btn-primary flex items-center whitespace-nowrap"
          disabled={loading}
        >
          <Plus className="w-4 h-4 mr-2" />
          添加参数
        </button>
      </div>

      {/* 参数列表 */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th className="text-left text-white font-semibold">参数名称</th>
                <th className="text-left text-white font-semibold">描述</th>
                <th className="text-left text-white font-semibold">默认值</th>
                <th className="text-left text-white font-semibold">范围</th>
                <th className="text-left text-white font-semibold">类型</th>
                <th className="text-left text-white font-semibold">可调</th>
                <th className="text-left text-white font-semibold">操作</th>
              </tr>
            </thead>
            <tbody>
              {parameters.map((param) => (
                <tr key={param.id} className="border-b border-white/10">
                  <td className="text-white font-medium">{param.name}</td>
                  <td className="text-white/70">{param.description || '-'}</td>
                  <td className="text-white">{param.default_value}</td>
                  <td className="text-white/70">
                    {param.range_min && param.range_max 
                      ? `${param.range_min} - ${param.range_max}`
                      : '-'
                    }
                  </td>
                  <td className="text-white/70">{getParameterTypeLabel(param.parameter_type)}</td>
                  <td>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      param.tunable 
                        ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                        : 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
                    }`}>
                      {param.tunable ? '是' : '否'}
                    </span>
                  </td>
                  <td>
                    <button
                      onClick={() => handleEdit(param.id)}
                      className="text-violet-400 hover:text-violet-300 mr-2"
                      disabled={loading}
                    >
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => handleDelete(param.id)}
                      className="text-red-400 hover:text-red-300"
                      disabled={loading}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 添加/编辑表单 */}
      {(showAddForm || editingParam) && (
        <div className="card">
          <ParameterEditForm
            formData={formData}
            setFormData={setFormData}
            onSave={handleSave}
            onCancel={handleCancel}
            loading={loading}
          />
        </div>
      )}

      {parameters.length === 0 && !showAddForm && (
        <div className="empty-state">
          <Settings className="w-16 h-16 text-white/30 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-white mb-2">暂无参数</h3>
          <p className="text-white/50 mb-6">开始添加策略参数</p>
          <button
            onClick={() => setShowAddForm(true)}
            className="btn-primary flex items-center whitespace-nowrap"
          >
            <Plus className="w-4 h-4 mr-2" />
            添加第一个参数
          </button>
        </div>
      )}
      
      {/* Toast */}
      <Toast
        message={toastMessage}
        isVisible={showToast}
        onClose={() => setShowToast(false)}
        duration={3000}
      />
      
      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={showConfirmDialog}
        title="删除参数"
        message="确定要删除这个参数吗？此操作不可撤销。"
        confirmText="删除"
        cancelText="取消"
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowConfirmDialog(false)
          setDeleteParamId(null)
        }}
        type="danger"
      />
    </div>
  )
}

interface ParameterEditFormProps {
  formData: any
  setFormData: (data: any) => void
  onSave: () => void
  onCancel: () => void
  loading: boolean
}

const ParameterEditForm: React.FC<ParameterEditFormProps> = ({
  formData,
  setFormData,
  onSave,
  onCancel,
  loading
}) => {
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-white">参数信息</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-white mb-2">参数名称</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full"
            placeholder="例如: 移动平均周期"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-white mb-2">参数类型</label>
          <select
            value={formData.parameter_type}
            onChange={(e) => setFormData({ ...formData, parameter_type: e.target.value })}
            className="w-full"
          >
            <option value="number">数值</option>
            <option value="string">字符串</option>
            <option value="boolean">布尔值</option>
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-white mb-2">默认值</label>
          <input
            type="text"
            value={formData.default_value}
            onChange={(e) => setFormData({ ...formData, default_value: e.target.value })}
            className="w-full"
            placeholder="例如: 20"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-white mb-2">最小值</label>
          <input
            type="number"
            value={formData.range_min}
            onChange={(e) => setFormData({ ...formData, range_min: e.target.value })}
            className="w-full"
            placeholder="例如: 5"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-white mb-2">最大值</label>
          <input
            type="number"
            value={formData.range_max}
            onChange={(e) => setFormData({ ...formData, range_max: e.target.value })}
            className="w-full"
            placeholder="例如: 100"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-white mb-2">可调参数</label>
          <div className="flex items-center">
            <button
              type="button"
              onClick={() => setFormData({ ...formData, tunable: !formData.tunable })}
              className="flex items-center"
            >
              {formData.tunable ? (
                <ToggleRight className="w-6 h-6 text-green-400" />
              ) : (
                <ToggleLeft className="w-6 h-6 text-gray-400" />
              )}
            </button>
            <span className="ml-2 text-sm text-white/70">
              {formData.tunable ? '是' : '否'}
            </span>
          </div>
        </div>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-white mb-2">描述</label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="w-full"
          rows={3}
          placeholder="参数描述..."
        />
      </div>
      
      <div className="flex justify-end space-x-3">
        <button onClick={onCancel} className="btn-secondary flex items-center whitespace-nowrap" disabled={loading}>
          <X className="w-4 h-4 mr-2" />
          取消
        </button>
        <button onClick={onSave} className="btn-primary flex items-center whitespace-nowrap" disabled={loading}>
          <Save className="w-4 h-4 mr-2" />
          {loading ? '保存中...' : '保存'}
        </button>
      </div>
    </div>
  )
}

export default ParameterConfig 